import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription, combineLatest, of, switchMap } from 'rxjs';
import { OrganizationMember } from '../../../../models/organization-member.model';
import { Organization } from '../../../../models/organization.model';
import { UserModel } from '../../../../models/user.model';
import { AuthService } from '../../../../services/auth.service';
import { ManagementService } from '../../../../services/management.service';
import * as ManagementActions from '../../../../state/actions/management.actions';
import * as ManagementSelectors from '../../../../state/selectors/management.selectors';
import { AppState } from '../../../../state/appstate';
import { AppOpenBannerComponent } from '../../../components/app-open-banner/app-open-banner.component';
import { BtnCallToActionComponent } from '../../../components/buttons/btn-call-to-action/btn-call-to-action.component';
import { DlgProfileUpdateComponent } from '../../../profile/dlg-profile-update/dlg-profile-update.component';

@Component({
  selector: 'app-org-invite',
  standalone: true,
  imports: [CommonModule, RouterModule, BtnCallToActionComponent, AppOpenBannerComponent],
  templateUrl: './org-invite.component.html',
  styleUrl: './org-invite.component.css',
})
export class OrgInviteComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private auth = inject(AuthService);
  private toastr = inject(ToastrService);
  private managementService = inject(ManagementService);
  private store = inject(Store<AppState>);

  orgId: string | null = null;
  organization: Organization | null = null;
  user: UserModel | null = null;

  get isLoggedIn(): boolean {
    return !!this.user?.uid || this.auth.isAuthenticated();
  }

  loading: boolean = true;
  actionInProgress$: Observable<boolean> = this.store.select(
    ManagementSelectors.selectMemberActionInProgress
  );

  private subs = new Subscription();
  private orgId$ = new BehaviorSubject<string | null>(null);

  // The signed-in user's existing membership (if any), so we never offer a duplicate request
  myMembership: OrganizationMember | null = null;

  ngOnInit(): void {
    this.subs.add(
      this.route.queryParamMap.subscribe((params) => {
        this.orgId = params.get('orgId');
        this.orgId$.next(this.orgId);
        if (this.orgId) {
          this.loadOrganization();
        } else {
          this.loading = false;
        }
      })
    );

    const user$ = this.store.select((s) => s.user.user);
    this.subs.add(
      user$.subscribe((u) => {
        this.user = u;
      })
    );

    this.subs.add(
      combineLatest([this.orgId$, user$])
        .pipe(
          switchMap(([orgId, u]) =>
            orgId && u?.uid ? this.managementService.getMyMembership(orgId, u.uid) : of(null)
          )
        )
        .subscribe((m) => {
          this.myMembership = m;
        })
    );
  }

  get isActiveMember(): boolean {
    return this.myMembership?.status === 'active';
  }

  get hasPendingRequest(): boolean {
    return this.myMembership?.status === 'requested';
  }

  get hasPendingInvite(): boolean {
    return this.myMembership?.status === 'invited';
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  loadOrganization(): void {
    if (!this.orgId) return;
    this.loading = true;
    this.managementService.getOrganization(this.orgId).subscribe({
      next: (org) => {
        this.organization = org;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onRequestJoin(): void {
    if (this.isActiveMember || this.hasPendingRequest) return;

    if (!this.isLoggedIn) {
      localStorage.setItem('redirectAfterLogin', `/links/orginvite?orgId=${this.orgId}`);
      this.router.navigate(['/auth/login']);
      return;
    }

    const uid = this.user?.uid || this.auth.getUid();
    if (!uid) {
      this.toastr.error('Please login to request membership.', 'Authentication Required');
      return;
    }

    if (!this.user?.name) {
      const dialogConfig = new MatDialogConfig();
      const dialogRef = this.dialog.open(DlgProfileUpdateComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          this.submitJoinRequest(uid);
        }
      });
    } else {
      this.submitJoinRequest(uid);
    }
  }

  private submitJoinRequest(uid: string): void {
    if (!this.orgId) return;

    this.store.dispatch(
      ManagementActions.requestOrganizationMembership({
        orgId: this.orgId,
        user: {
          uid,
          name: this.user?.name,
          email: this.user?.email,
          photoURL: this.user?.photoURL,
          contact: this.user?.contact,
          address: this.user?.address,
          profession: this.user?.profession,
          gender: this.user?.gender,
        },
      })
    );
  }
}
